import { App } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { authApi } from '../api/auth.api'
import { impersonationApi } from '../api/impersonation.api'
import { tenantApi } from '../api/tenant.api'
import { useAuthStore } from '../stores/auth.store'
import { useImpersonationStore } from '../stores/impersonation.store'
import { buildImpersonatedUser } from '../utils/impersonated-user'
import { ROUTE_PATHS } from '@/config/app.config'
import { resolveLandingPath } from '@/config/navigation'
import { UserRole } from '@/lib/rbac'
import { ApiError } from '@/lib/api'

/** The account a super admin wants to enter. */
export interface EnterAccountTarget {
  /** Target tenant id. */
  tenantId: string
  /** Human-friendly account name, shown in the confirm modal. */
  name: string
}

/** Reactive snapshot of the current impersonation session. */
export function useImpersonation() {
  const session = useImpersonationStore((s) => s.session)
  return { session, isImpersonating: session !== null }
}

/**
 * Controller for entering an account with admin access. Returns a function
 * that opens the confirm modal and, on confirm, swaps to the impersonation
 * token, replaces the store `user` with the impersonated account admin (so the
 * whole app behaves like that admin logged in — see {@link buildImpersonatedUser}),
 * clears the super-admin-scoped query cache, and lands on the account's default
 * page. The modal's OK button spins while this runs, so there is no flash of the
 * platform view before the swap completes.
 */
export function useEnterAccount() {
  const { modal, message } = App.useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const startSession = useImpersonationStore((s) => s.start)

  const mutation = useMutation({
    mutationFn: (tenantId: string) => impersonationApi.start(tenantId),
  })

  return (account: EnterAccountTarget) => {
    modal.confirm({
      title: `Enter ${account.name}?`,
      content:
        "You'll manage this account with admin access as yourself. Your actions are recorded " +
        'against your super admin account, and you can exit at any time.',
      okText: 'Enter account',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const result = await mutation.mutateAsync(account.tenantId)
          const { user: realUser, setUser, setTenant } = useAuthStore.getState()

          // Swap the active token first, so the tenant-detail fetch below (and
          // everything after) is scoped to the impersonated account.
          startSession({
            token: result.token,
            tenant: result.tenant,
            mode: result.impersonation.mode,
            // Snapshot the super admin's own authorization identity BEFORE
            // `buildImpersonatedUser` overwrites it below — it is the only
            // record of who is really driving the session (see `RealIdentity`).
            realIdentity: realUser,
          })
          // Drop every super-admin-scoped entry; data refetches under the imp token.
          queryClient.clear()

          // Pull the tenant's sector + reporting group so the effective user and
          // cached tenant detail are ready before the account view renders.
          const detail = await tenantApi.detail(result.tenant.id)
          if (realUser) setUser(buildImpersonatedUser(realUser, result.tenant, detail))
          setTenant(detail)

          // Land on the first page the impersonated account admin can see.
          await navigate({ to: resolveLandingPath({ role: UserRole.AdminUser }) })
        } catch (err) {
          message.error(err instanceof ApiError ? err.message : 'Failed to enter account')
          // Re-throw so the modal stays open on failure.
          throw err
        }
      },
    })
  }
}

/**
 * Controller for exiting impersonation. Calls the exit endpoint WITH the
 * impersonation token (the interceptor attaches it while the session is still
 * set), then restores the super admin's own session (re-fetch `/auth/me` under
 * the admin token) and returns to the platform (all-accounts) view. The exit is
 * idempotent, so a failed call (e.g. an expired token) still drops the local
 * session.
 */
export function useExitAccount() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const stopSession = useImpersonationStore((s) => s.stop)

  const mutation = useMutation({
    mutationFn: () => impersonationApi.exit(),
  })

  const exit = async () => {
    try {
      await mutation.mutateAsync()
    } catch {
      // Idempotent server-side — exit locally even if the call failed.
    } finally {
      stopSession()
      queryClient.clear()
      // Restore the super admin profile now that the admin token is active again.
      try {
        const profile = await authApi.me()
        const { setUser, setTenant } = useAuthStore.getState()
        setUser(profile)
        setTenant(null)
      } catch {
        // If it fails, the axios interceptor / bootstrap will recover the session.
      }
      await navigate({ to: ROUTE_PATHS.accounts })
    }
  }

  return { exit, isPending: mutation.isPending }
}
